import { useEffect, useMemo } from 'react';

import { DispatchAction } from '../actions';
import { ConsoleApiClient, ConsoleLogger, GetAuthenticationToken, LocationApi } from '../types';
import { State } from '../state';
import { createCloudinaryFetchClient } from '../../api/createCloudinaryFetchClient';
import { createConsoleApiClient } from '../../api/createConsoleApiClient';
import { EnvironmentData, ExternalLogger } from '@cld/unicorn-api';

export const useChangeProductEnvironment = ({
  consoleApiClient,
  dispatch,
  state,
  environmentData,
  localStorageClient,
  getAuthenticationToken,
  consoleLogger,
  locationApi,
  unicornExternalLogger,
}: {
  consoleApiClient: ConsoleApiClient;
  dispatch: DispatchAction;
  state: State;
  consoleLogger: ConsoleLogger;
  environmentData: EnvironmentData;
  getAuthenticationToken: GetAuthenticationToken;
  localStorageClient: Storage;
  locationApi: LocationApi;
  unicornExternalLogger: ExternalLogger;
}) => {
  const productEnvironmentChangeState = state.applicationStatus === 'ready' ? state.productEnvironmentChangeState : undefined;

  useEffect(() => {
    if (productEnvironmentChangeState?.status === 'pending') {
      const cloudinaryFetchClient = createCloudinaryFetchClient({
        // FIXME: inject fetch
        fetchFunction: window.fetch,
        pageOrigin: environmentData.origin,
        productEnvironmentExternalId: productEnvironmentChangeState.newExternalId,
        storage: localStorageClient,
        getAuthenticationToken,
        redirect: locationApi.redirect,
        consoleLogger,
      })();

      const consoleApiClient = createConsoleApiClient({
        backendUrl: environmentData.backendUrls.console,
        consoleLogger,
        unicornExternalLogger,
        cloudinaryFetchClient,
      });

      void Promise.all([
        consoleApiClient.fetchProductEnvironment(productEnvironmentChangeState.newExternalId),
        consoleApiClient.fetchLoginFromSession(),
      ]).then(([productEnvironmentResponse, loginFromSessionResponse]) => {
        dispatch({
          type: 'productEnvironmentChangeFinished',
          payload:
            productEnvironmentResponse.kind === 'success' && loginFromSessionResponse.kind === 'success'
              ? {
                  kind: 'success',
                  productEnvironment: productEnvironmentResponse.data,
                  capabilities: loginFromSessionResponse.data.capabilities,
                  deprecatedData: {
                    jwt: loginFromSessionResponse.data.jwt,
                    cmsMode: loginFromSessionResponse.data.cmsMode,
                    sessionNotifications: loginFromSessionResponse.data.sessionNotifications,
                    userEvents: loginFromSessionResponse.data.userEvents,
                    rootFolderRole: loginFromSessionResponse.data.rootFolderRole,
                    intercomHash: loginFromSessionResponse.data.intercomHash,
                    mediaLibraryPermissions: loginFromSessionResponse.data.mediaLibraryPermissions,
                  },
                }
              : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, productEnvironmentChangeState]);
};
